/* FABEL — theme.js : shared utilities, drawer primitive, reveal animations. */
(function () {
  'use strict';

  const FABEL = (window.FABEL = window.FABEL || {});

  /* ---------------------------------------------------------------- pubsub */
  const subscribers = {};
  FABEL.subscribe = function (event, cb) {
    (subscribers[event] = subscribers[event] || []).push(cb);
    return () => {
      subscribers[event] = subscribers[event].filter((fn) => fn !== cb);
    };
  };
  FABEL.publish = function (event, data) {
    (subscribers[event] || []).forEach((cb) => cb(data));
  };

  /* ----------------------------------------------------------------- utils */
  FABEL.FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  FABEL.debounce = function (fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, arguments), wait);
    };
  };

  FABEL.fetchConfig = function (type) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type || 'json'}`
      }
    };
  };

  let lockCount = 0;
  let scrollY = 0;
  FABEL.lockScroll = function () {
    if (lockCount === 0) {
      scrollY = window.scrollY;
      document.body.classList.add('is-locked');
      document.body.style.top = `-${scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
    lockCount += 1;
  };
  FABEL.unlockScroll = function () {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      document.body.classList.remove('is-locked');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    }
  };

  FABEL.trapFocus = function (container, opener) {
    const focusables = () =>
      Array.from(container.querySelectorAll(FABEL.FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );

    function handler(event) {
      if (event.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', handler);
    const target =
      container.querySelector('[data-autofocus]') || focusables()[0] || container;
    window.requestAnimationFrame(() => {
      try {
        target.focus({ preventScroll: true });
      } catch (e) {
        /* noop */
      }
    });

    return function release() {
      container.removeEventListener('keydown', handler);
      if (opener && document.body.contains(opener)) {
        try {
          opener.focus({ preventScroll: true });
        } catch (e) {
          /* noop */
        }
      }
    };
  };

  FABEL.announce = function (message) {
    const region = document.getElementById('fabel-live-region');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 80);
  };

  /* ------------------------------------------------------------ overlay -- */
  function overlayEl() {
    let el = document.getElementById('fabel-overlay');
    if (!el) {
      el = document.createElement('div');
      el.id = 'fabel-overlay';
      el.className = 'overlay';
      el.setAttribute('aria-hidden', 'true');
      document.body.appendChild(el);
    }
    return el;
  }
  FABEL.showOverlay = function () {
    overlayEl().setAttribute('data-open', 'true');
  };
  FABEL.hideOverlay = function () {
    overlayEl().removeAttribute('data-open');
  };

  /* -------------------------------------------------------------- drawer -- */
  class FabelDrawer extends HTMLElement {
    constructor() {
      super();
      this.releaseFocus = null;
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.setAttribute('role', this.getAttribute('role') || 'dialog');
      this.setAttribute('aria-modal', 'true');
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) {
          event.preventDefault();
          this.close();
        }
      });
    }

    get isOpen() {
      return this.getAttribute('data-open') === 'true';
    }

    open(opener) {
      if (this.isOpen) return;
      this.opener = opener || document.activeElement;
      this.setAttribute('data-open', 'true');
      this.removeAttribute('aria-hidden');
      FABEL.showOverlay();
      FABEL.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      FABEL.activeDrawer = this;
      this.releaseFocus = FABEL.trapFocus(this, this.opener);
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKeydown);
      if (FABEL.activeDrawer === this) FABEL.activeDrawer = null;
      if (!document.querySelector('fabel-drawer[data-open="true"], [data-open="true"].search-modal')) {
        FABEL.hideOverlay();
      }
      FABEL.unlockScroll();
      if (this.releaseFocus) {
        this.releaseFocus();
        this.releaseFocus = null;
      }
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        this.close();
      }
    }
  }
  FABEL.FabelDrawer = FabelDrawer;
  if (!customElements.get('fabel-drawer')) customElements.define('fabel-drawer', FabelDrawer);

  /* Global open triggers: [data-drawer-toggle="drawer-id"] */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-toggle]');
    if (trigger) {
      const drawer = document.getElementById(trigger.getAttribute('data-drawer-toggle'));
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(trigger);
      }
      return;
    }
    if (event.target.id === 'fabel-overlay') {
      const open = document.querySelector(
        'fabel-drawer[data-open="true"], cart-drawer[data-open="true"], mobile-menu[data-open="true"], search-modal[data-open="true"]'
      );
      if (open && typeof open.close === 'function') open.close();
    }
  });

  /* ---------------------------------------------------------- accordions -- */
  /* <details> handles the semantics; we only animate height-safe padding. */

  /* ------------------------------------------------------- reveal on view - */
  function initReveal() {
    if (document.documentElement.classList.contains('no-animations')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const items = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!items.length || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
          window.setTimeout(() => el.classList.add('is-visible'), delay);
          observer.unobserve(el);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));

    // Vangnet: blijft een element om welke reden dan ook onopgemerkt, toon het
    // alsnog. Content mag nooit onzichtbaar blijven door een animatie.
    window.setTimeout(() => {
      items.forEach((el) => {
        if (!el.classList.contains('is-visible')) {
          el.classList.add('is-visible');
          observer.unobserve(el);
        }
      });
    }, 2500);
  }

  FABEL.initReveal = initReveal;
  document.addEventListener('DOMContentLoaded', initReveal);
  document.addEventListener('shopify:section:load', initReveal);

  /* --------------------------------------------------- horizontal snapping */
  FABEL.observeSlides = function (container, onChange) {
    if (!('IntersectionObserver' in window)) return null;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) onChange(entry.target);
        });
      },
      { root: container, threshold: 0.6 }
    );
    Array.from(container.children).forEach((child) => observer.observe(child));
    return observer;
  };

  /* --------------------------------------------------------- quantity UI -- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-qty]');
        if (!button) return;
        event.preventDefault();
        const step = button.getAttribute('data-qty') === 'plus' ? 1 : -1;
        const min = parseInt(this.input.getAttribute('min') || '0', 10);
        const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
        if (next === parseInt(this.input.value, 10)) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* --------------------------------------------------------------- modal -- */
  class FabelModal extends HTMLElement {
    connectedCallback() {
      this.releaseFocus = null;
      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]') || event.target === this) this.close();
      });
    }
    open(opener) {
      this.setAttribute('data-open', 'true');
      this.removeAttribute('aria-hidden');
      FABEL.lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      this.releaseFocus = FABEL.trapFocus(this, opener);
    }
    close() {
      if (this.getAttribute('data-open') !== 'true') return;
      this.removeAttribute('data-open');
      this.setAttribute('aria-hidden', 'true');
      FABEL.unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.releaseFocus) this.releaseFocus();
    }
  }
  if (!customElements.get('fabel-modal')) customElements.define('fabel-modal', FabelModal);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-toggle]');
    if (!trigger) return;
    const modal = document.getElementById(trigger.getAttribute('data-modal-toggle'));
    if (modal && typeof modal.open === 'function') {
      event.preventDefault();
      modal.open(trigger);
    }
  });
})();
